import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  LinearProgress,
  Typography
} from '@material-ui/core';
import { scaleLinear } from 'd3-scale';
import { groupBy, isEqual, round, throttle, times } from 'lodash';
import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import {
  PDFDocumentProxy,
  PDFPageProxy
} from 'pdfjs-dist/types/src/display/api';
import {
  createRef,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { HotKeys, KeySequence } from 'react-hotkeys';
import { useId } from '../../hooks/useId';
import { useIsVisibleForRef } from '../../hooks/useIsVisible';
import { usePromise } from '../../hooks/usePromise';
import { useUnsafeTranslation } from '../../i18next';
import { Centered } from '../../layout/Centered';
import { FlexContainer } from '../../layout/Flex';
import { pdfjs } from '../../services/pdfjs';
import { DialogActionsHeader } from '../DialogActionsHeader';
import { DialogActionsWithSlots } from '../DialogActionsWithSlots';
import { Loader } from '../Loader';

export type PdfFile = {
  url: string;
};

const usePdfDocumentFromUrl = (url: string) => {
  return usePromise(async () => (await pdfjs()).getDocument(url).promise, [
    url
  ]);
};

const usePage = (doc: PDFDocumentProxy, page: number) => {
  return usePromise(() => {
    return doc.getPage(page);
  }, [doc, page]);
};

const PagesContainerOuter = styled('div')((p) => ({
  width: '100%',
  height: '100%'
}));

const PagesContainerInner = styled('div')((p) => ({
  width: '100%',
  height: '100%'
}));

const PageContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  position: 'relative'
}));

const PageOverlayContainer = styled('div')((p) => ({
  position: 'absolute',
  left: '50%',
  top: '50%'
}));

type RenderTask = ReturnType<PDFPageProxy['render']>;
type RenderFn = () => RenderTask;
class RenderingQueue {
  state: 'RUNNING' | 'READY' = 'READY';
  queue: RenderFn[] = [];

  push(fn: RenderFn): void {
    this.queue.push(fn);
    this.tryRun();
  }

  tryRun() {
    if (this.state === 'RUNNING') {
      return;
    }
    const nextItem = this.queue.shift();
    if (nextItem) {
      this.state = 'RUNNING';
      nextItem().promise.finally(() => {
        this.state = 'READY';
        this.tryRun();
      });
    }
  }
}

const RENDERING_QUEUE = new RenderingQueue();

const toPageId = (prefix: string, page: number) => `${prefix}${page}`;

const Page = ({
  doc,
  page,
  scale,
  upscaleFactor,
  pageId,
  scrollRef,
  pageRef
}: {
  doc: PDFDocumentProxy;
  page: number;
  scale: number;
  upscaleFactor: number;
  pageId: string;
  scrollRef: React.RefObject<HTMLDivElement>;
  pageRef: React.RefObject<HTMLDivElement>;
}) => {
  const { t } = useUnsafeTranslation();
  const { isVisible } = useIsVisibleForRef(pageRef, {
    root: scrollRef.current || null,
    rootMargin: '500px'
  });
  const theme = useTheme();
  // We're using dynamic scaling.
  // The easiest solution would be to always re-render the canvas
  // when the zoom level changes.
  // This causes flicker and is slow.
  // We therefore render through a CSS transform.
  // To have a sharper display, we render an upscaled version of the requested scale.
  // The upscale factor is determined by the zoom level. If we're really far out,
  // we don't need to upscale, if we're really far in, we upscale even more.
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const paramsOfLastRendering = useRef<{
    page: number;
    upscaleFactor: number;
  } | null>(null);
  const [p, loading, error] = usePage(doc, page);
  const [rendering, setRendering] = useState(false);
  useEffect(() => {
    const nextRenderParams = { page, upscaleFactor };
    if (isEqual(paramsOfLastRendering.current, nextRenderParams)) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    if (!p) {
      return;
    }

    const viewport = p.getViewport({ scale: 1 * upscaleFactor });
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    if (isVisible) {
      console.log(`Page ${page} rendering queued`);
      setRendering(true);
      RENDERING_QUEUE.push(() => {
        paramsOfLastRendering.current = nextRenderParams;
        console.log(`Page ${page} rendering start`);
        console.time(`render ${page}`);

        const offScreenCanvas = document.createElement('canvas');
        offScreenCanvas.width = canvas.width;
        offScreenCanvas.height = canvas.height;
        const offScreenCtx = offScreenCanvas.getContext('2d', {
          alpha: false
        })!;

        const task = p.render({
          canvasContext: offScreenCtx,
          viewport
        });

        task.promise.finally(() => {
          ctx.drawImage(offScreenCanvas, 0, 0);
          console.timeEnd(`render ${page}`);
          console.log(`Page ${page} rendering done`);
          setRendering(false);
        });

        return task;
      });
    }
  }, [p, upscaleFactor, isVisible, page]);

  const { canvasStyle } = useMemo(() => {
    return {
      canvasStyle: {
        border: theme.custom.border.standard,
        borderWidth: Math.max(1, 1 / scale) * upscaleFactor,
        marginTop: (theme.spacing(2) / scale) * upscaleFactor
      }
    };
  }, [scale, upscaleFactor, theme]);

  return (
    <>
      <PageContainer ref={pageRef} id={pageId}>
        {loading && !p && <div style={{ minHeight: 800 }} />}
        {p ? <canvas ref={canvasRef} style={canvasStyle} /> : null}
        {rendering && (
          <PageOverlayContainer>
            <Loader />
          </PageOverlayContainer>
        )}
        {error && (
          <Centered>
            <Typography variant="caption">
              {t('Failed to load page.')}
            </Typography>
          </Centered>
        )}
      </PageContainer>
    </>
  );
};

const Pages = ({
  doc,
  scale,
  setScale,
  pageIdPrefix,
  scrollRef,
  pageRefs
}: {
  doc: PDFDocumentProxy;
  scale: number;
  setScale: React.Dispatch<SetStateAction<number>>;
  pageIdPrefix: string;
  scrollRef: React.RefObject<HTMLDivElement>;
  pageRefs: React.RefObject<HTMLDivElement>[];
}) => {
  // We're using dynamic scaling.
  // The easiest solution would be to always re-render the canvas
  // when the zoom level changes.
  // This causes flicker and is slow.
  // We therefore render through a CSS transform.
  // To have a sharper display, we render an upscaled version of the requested scale.
  // The upscale factor is determined by the zoom level. If we're really far out,
  // we don't need to upscale, if we're really far in, we upscale even more.
  const containerRef = useRef<HTMLDivElement>(null);
  const throttledSetScale = useMemo(() => throttle(setScale, 25), [setScale]);
  const [upscaleFactor, setUpscaleFactor] = useState(3);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const wheelListener = (e: WheelEvent) => {
        if (!e.ctrlKey && !e.metaKey) {
          return;
        }
        e.preventDefault();

        const delta = e.deltaY;
        if (delta > 0) {
          throttledSetScale(increaseScale);
        } else {
          throttledSetScale(decreaseScale);
        }
      };
      container.addEventListener('wheel', wheelListener);
      return () => {
        container.removeEventListener('wheel', wheelListener);
      };
    }
  }, [throttledSetScale]);

  useEffect(() => {
    if (scale > 3.5) {
      setUpscaleFactor((x) => Math.max(5, x));
      // } else if (scale < 0.5) {
      //   setUpscaleFactor(1);
    } else {
      setUpscaleFactor((x) => Math.max(3, x));
    }
  }, [scale, setUpscaleFactor]);

  const { containerStyle } = useMemo(() => {
    return {
      containerStyle: {
        transform: `scale(${scale / upscaleFactor}`,
        transformOrigin: 'top'
      }
    };
  }, [scale, upscaleFactor]);

  return (
    <PagesContainerOuter ref={containerRef}>
      <PagesContainerInner style={containerStyle}>
        {times(doc.numPages).map((i) => (
          <Page
            pageId={toPageId(pageIdPrefix, i + 1)}
            key={i}
            doc={doc}
            page={i + 1}
            scale={scale}
            upscaleFactor={upscaleFactor}
            scrollRef={scrollRef}
            pageRef={pageRefs[i]}
          />
        ))}
      </PagesContainerInner>
    </PagesContainerOuter>
  );
};

const ProgressContainer = styled('div')((p) => ({
  maxWidth: 500,
  minWidth: 200,
  textAlign: 'center'
}));

const normaliseProgress = (value: number, min: number, max: number) =>
  ((value - min) * 100) / (max - min);

const PageNav = ({
  page,
  totalPages,
  setPage
}: {
  page: number;
  totalPages: number;
  setPage: (nextPage: number) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const progressRef = useRef<HTMLDivElement>(null);
  return (
    <FlexContainer direction="column" spacing={0}>
      <FlexContainer justifyContent="center">
        <IconButton disabled={page === 1} onClick={() => setPage(page - 1)}>
          <ChevronLeft size={20} />
        </IconButton>
        <ProgressContainer>
          <LinearProgress
            ref={progressRef}
            color="primary"
            value={normaliseProgress(page, 0, totalPages)}
            variant="determinate"
            style={{ flex: 1, cursor: 'pointer' }}
            role="button"
            onClick={(ev) => {
              if (!progressRef.current) {
                return;
              }
              const rect = progressRef.current.getBoundingClientRect();
              const left = rect.x;
              const right = rect.x + rect.width;
              const scale = scaleLinear([1, totalPages]).domain([left, right]);
              const nextPage = scale(ev.clientX);
              if (nextPage !== undefined) {
                setPage(Math.ceil(nextPage));
              }
            }}
          />
        </ProgressContainer>
        <IconButton
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          <ChevronRight size={20} />
        </IconButton>
      </FlexContainer>
      <Typography variant="caption">
        {t('Page')} {page}/{totalPages}
      </Typography>
    </FlexContainer>
  );
};

const SCALE_DELTA = 1.1;
const SCALE_MIN = 0.4;
const SCALE_MAX = 10;

const increaseScale = (scale: number, steps = 1) => {
  let nextScale = scale;
  do {
    nextScale = round(nextScale * SCALE_DELTA, 2);
    nextScale = Math.ceil(nextScale * 10) / 10;
    nextScale = Math.min(SCALE_MAX, nextScale);
  } while (--steps > 0 && nextScale < SCALE_MAX);
  return nextScale;
};

const decreaseScale = (scale: number, steps = 1) => {
  let nextScale = scale;
  do {
    nextScale = round(nextScale / SCALE_DELTA, 2);
    nextScale = Math.floor(nextScale * 10) / 10;
    nextScale = Math.max(SCALE_MIN, nextScale);
  } while (--steps > 0 && nextScale > SCALE_MIN);
  return nextScale;
};

const overlappingYPixels = (
  a: { top: number; bottom: number },
  b: { top: number; bottom: number }
) => {
  return Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
};

const findMostVisiblePage = (
  containerRef: React.RefObject<HTMLDivElement>,
  pageRefs: React.RefObject<HTMLDivElement>[],
  moveDirection: 'DOWN' | 'UP'
): number | null => {
  const c = containerRef.current;
  if (!c) {
    return null;
  }
  if (!pageRefs.length) {
    return null;
  }

  // TODO need to work on that - it doesn't work reliably.
  // Issues start when we're completely zoomed out and several
  // pages are shown at the same time.
  // Hard to find a deterministic solution in this case,
  // as scrollIntoView also won't work anymore.
  const containerRect = c.getBoundingClientRect();
  const overlaps: [number, number][] = [];

  // Collect all overlapping pages.
  // Found those with the maximum overlap
  // When there are several, take the move direction into account.
  // If we're going up we take the first match, if we're going down we take the last match.
  // This allows us to reach the last page when scrolling down, but also the first page
  // when scrolling up, even if several pages are fully shown.
  for (let index = 0; index < pageRefs.length; index++) {
    const pRect = pageRefs[index]?.current?.getBoundingClientRect();
    if (pRect) {
      // Ceil because sometimes floats make a page smaller by a tiny fraction
      const nextOverlap = Math.ceil(overlappingYPixels(containerRect, pRect));
      if (nextOverlap) {
        overlaps.push([index + 1, nextOverlap]);
        continue;
      }
      if (overlaps.length && !nextOverlap) {
        break; // no more matches
      }
    }
  }
  const groupedByOverlap = groupBy(overlaps, (o) => o[1]);
  const maxOverlap = Math.max(...overlaps.map((o) => o[1]));
  const allOverlapping = groupedByOverlap[maxOverlap] || [];
  return (
    (moveDirection === 'UP'
      ? allOverlapping[0]
      : allOverlapping[allOverlapping.length - 1])?.[0] ?? null
  );
};

export type PdfDialogState =
  | {
      ready: false;
    }
  | {
      ready: true;
      currentPage: number;
      totalPages: number;
    };

export type PdfViewerKeyMap = Partial<{
  nextPage: KeySequence;
  prevPage: KeySequence;
  firstPage: KeySequence;
  lastPage: KeySequence;
  zoomIn: KeySequence;
  zoomOut: KeySequence;
}>;

export const PDF_VIEWER_DEFAULT_KEYMAP: PdfViewerKeyMap = {
  nextPage: 'right',
  prevPage: 'left',

  firstPage: 'home',
  lastPage: 'end',

  zoomIn: 'shift+-',
  zoomOut: '-'
};

/*
const computeFixedSizes = () => {
  const el = document.getElementById(
    toPageId(pageIdPrefix, page)
  );
  const pageRect = el?.getBoundingClientRect();
  const contRect = scrollRef.current?.getBoundingClientRect();
  if (!pageRect || !contRect) {
    return;
  }
  const scaleX = contRect.width / pageRect.width;
  const scaleY = contRect.height / pageRect.height;

  const scaleToFit = Math.min(scaleX, scaleY);
  const scaleToCover = Math.max(scaleX, scaleY);

  console.log(scaleToFit, scaleToCover);
}
*/

export const PdfDialog = ({
  title,
  file,
  actions,
  onClose,
  keyMap,
  ...dialogProps
}: Omit<DialogProps, 'onClose'> & {
  title: React.ReactNode;
  file: PdfFile;
  onClose: () => void; // override, because we pass this on and don't want to provide the normal arguments to it
  actions?: (props: {
    state: PdfDialogState;
    close: () => void;
  }) => React.ReactNode;
  keyMap?: PdfViewerKeyMap;
}) => {
  const { t } = useUnsafeTranslation();
  const [doc, loading, error] = usePdfDocumentFromUrl(file.url);
  const [scale, setScale] = useState<number>(1.0);
  const prevScale = useRef(scale);
  const id = useId();
  const pageIdPrefix = useMemo(() => `pdf-page-prefix-${id}-`, [id]);
  const moveDirection = useRef<'DOWN' | 'UP'>('DOWN');
  const [page, setPage] = useState(1);

  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollPos = useRef(0);
  const pageRefs = useMemo(() => {
    if (!doc) {
      return [];
    }
    return times(doc.numPages).map(() => createRef<HTMLDivElement>());
  }, [doc]);

  const currentPageId = toPageId(pageIdPrefix, page);
  const scrollToPage = useCallback(
    (nextPage: number) => {
      document.getElementById(currentPageId)?.scrollIntoView();
      const c = scrollRef.current;
      // Update the last scroll position - this is a way for us to indicate
      // to the scroll handler, that we're not interested in checking for
      // page changes, as we triggered one ourselves.
      if (c) {
        lastScrollPos.current = c.scrollTop;
      }
      setPage(nextPage);
    },
    [currentPageId, scrollRef]
  );

  useEffect(() => {
    const c = scrollRef.current;
    if (!c) {
      return;
    }
    const handler = (ev: Event) => {
      const nextScrollPos = c.scrollTop;
      // No change, nothing to do.
      // Mostly happening when scroll was programmatically updated.
      if (nextScrollPos === lastScrollPos.current) {
        return;
      }
      moveDirection.current =
        nextScrollPos > lastScrollPos.current ? 'DOWN' : 'UP';
      lastScrollPos.current = nextScrollPos;
      const nextPage = findMostVisiblePage(
        scrollRef,
        pageRefs,
        moveDirection.current
      );
      if (nextPage !== null) {
        setPage(nextPage);
      }
    };
    const throttledHandler = throttle(handler, 400);
    c.addEventListener('scroll', throttledHandler);
    return () => c.removeEventListener('scroll', throttledHandler);
  }, [setPage, pageRefs]);

  const renderedActions = useMemo(() => {
    if (!actions) {
      return undefined;
    }
    const state: PdfDialogState = doc
      ? {
          ready: true,
          currentPage: page,
          totalPages: doc.numPages
        }
      : {
          ready: false
        };
    return actions({ state, close: onClose });
  }, [actions, page, doc, onClose]);

  useEffect(() => {
    // Avoid to trigger this on the initial run
    if (prevScale.current === scale) {
      return;
    }
    prevScale.current = scale;
    const nextPage = findMostVisiblePage(
      scrollRef,
      pageRefs,
      moveDirection.current
    );
    if (nextPage !== null) {
      setPage(nextPage);
    }
  }, [scale, scrollRef, pageRefs]);

  useLayoutEffect(() => {
    scrollRef.current && scrollRef.current.focus();
  });

  const keyHandlers = useMemo(() => {
    return {
      nextPage: () => doc && page < doc.numPages && scrollToPage(page + 1),
      prevPage: () => doc && page > 1 && scrollToPage(page - 1),
      firstPage: () => doc && scrollToPage(1),
      lastPage: () => doc && scrollToPage(doc.numPages),

      zoomIn: () => setScale(increaseScale),
      zoomOut: () => setScale(decreaseScale)
    };
  }, [doc, page, scrollToPage]);

  return (
    <HotKeys handlers={keyHandlers} keyMap={keyMap} allowChanges={true}>
      <Dialog {...dialogProps}>
        <DialogActionsHeader
          onClose={onClose}
          left={
            <Typography variant="h4" component="h1">
              {title}
            </Typography>
          }
        />
        <DialogContent
          ref={scrollRef}
          tabIndex={-1}
          classes={{
            root: css({
              ':focus-visible': {
                outline: 'none'
              }
            })
          }}
        >
          {loading && <Loader height="100%" />}
          {error && <Centered height={500}>{t('Failed to load PDF')}</Centered>}
          {doc && (
            <Pages
              doc={doc}
              scale={scale}
              setScale={setScale}
              pageIdPrefix={pageIdPrefix}
              scrollRef={scrollRef}
              pageRefs={pageRefs}
            />
          )}
        </DialogContent>
        {doc && (
          <DialogActionsWithSlots
            variant="compact"
            left={
              <FlexContainer fullWidth justifyContent="center">
                <IconButton onClick={() => setScale(decreaseScale)}>
                  <Minus size={20} />
                </IconButton>
                <FlexContainer style={{ minWidth: 70 }} justifyContent="center">
                  <Typography variant="caption">
                    {round(scale * 100, 1)}%
                  </Typography>
                </FlexContainer>

                <IconButton onClick={() => setScale(increaseScale)}>
                  <Plus size={20} />
                </IconButton>
              </FlexContainer>
            }
            center={
              <PageNav
                page={page}
                setPage={scrollToPage}
                totalPages={doc.numPages}
              />
            }
            right={renderedActions}
          />
        )}
      </Dialog>
    </HotKeys>
  );
};
